import React, { useEffect, useRef, useState } from "react";
import Common from "../../../Components/Layouts/Common.js";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../../Hooks/useApi.js";
import Button from "../../../Components/UI/Buttons/Button.js";
import { handleApiError } from "../../../Helpers/ErrorHandler.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

function ViewKIIQuestionnaire() {
  const location = useLocation();
  const { id, viewType } = location?.state || {};
  const scrollContainerRef = useRef(null);
  const [questionnaireMeta] = useState(id ? { id } : null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [questionnaireData, setQuestionnaireData] = useState();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { request } = useApi();

  const fetchQuestionnaireData = async () => {
    setLoading(true);
    try {
      const response = await request(
        "GET",
        `/v1/kii/questionnaires/questions/${questionnaireMeta?.id}`,
        {},
        "kii_service"
      );
      response.questions && response.questions.length
        ? setQuestionnaireData({
            ...response,
            sections: [
              {
                sectionName: "Without Section",
                sectionOrder: -1,
                questions: response.questions,
              },
              ...response.sections,
            ],
          })
        : setQuestionnaireData(response);
    } catch (error) {
      console.error("Error fetching data:", error);
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (questionnaireMeta?.id) {
      fetchQuestionnaireData();
    }
  }, [questionnaireMeta?.id]);

  // Roman numeral converter
  const toRoman = (num) => {
    const roman = [
      "I",
      "II",
      "III",
      "IV",
      "V",
      "VI",
      "VII",
      "VIII",
      "IX",
      "X",
      "XI",
      "XII",
      "XIII",
      "XIV",
      "XV",
      "XVI",
      "XVII",
      "XVIII",
      "XIX",
      "XX",
    ];
    return roman[num] || num + 1; // Fallback for large numbers
  };
  const markCompleteHandler = async () => {
    try {
      const response = await request(
        "POST",
        `/v1/kii/questionnaires/finalize`,
        {
          data: {
            questionnaireId: id,
            status: "COMPLETED",
          },
        },
        "kii_service"
      );

      if (response) {
        navigate("/kii-questionnaire");
      }
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  };

  const makeChangesHandler = () => {
    try {
      setTimeout(() => {
        navigate("/create-kii-questionnaire", {
          state: {
            id: id,
            name: questionnaireData?.name,
          },
        });
      });
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  };
  const fromTemplateHandler = () => {
    try {
      setTimeout(() => {
        navigate("/create-kii-questionnaire", {
          state: {
            id: id,
            name: questionnaireData?.name,
            isTemplate: true,
          },
        });
      });
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  };

  const slideLeft = () => {
    if (scrollContainerRef.current && questionnaireData?.sections?.length > 0) {
      scrollContainerRef.current.scrollBy({ left: -128, behavior: "smooth" });
      setSelectedIndex((prevIndex) => Math.max(0, prevIndex - 1));
    }
  };

  
  const slideRight = () => {
    if (scrollContainerRef.current && questionnaireData?.sections?.length > 0) {
      scrollContainerRef.current.scrollBy({ left: 128, behavior: "smooth" });
      setSelectedIndex((prevIndex) =>
        Math.min(questionnaireData?.sections?.length - 1, prevIndex + 1)
      );
    }
  };

  return (
    <Common headline={`View KII Questionnaire `}>
      <div className="container mx-auto p-4">
        <div className=" bg-white shadow-lg rounded-lg overflow-hidden ">
          <div className="p-6">
            {loading ? (
              <p className="text-center text-gray-500">Loading...</p>
            ) : (
              <>
                {/* Questionnaire Header */}
                <div className="flex mb-4 ">
                  <h4 className=" font-bold text-gray-800 pr-2">
                    Questionnaire Name:
                  </h4>
                  <h5>{questionnaireData?.name}</h5>
                </div>

                {/* Tabs for Sections */}
                <div className="flex gap-5">
                  <button
                    onClick={slideLeft}
                    disabled={selectedIndex === 0}
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <div
                    ref={scrollContainerRef}
                    className="flex items-center overflow-auto whitespace-nowrap border-b max-w-7xl"
                  >
                    {questionnaireData?.sections
                      .sort((a, b) => a.sectionOrder - b.sectionOrder)
                      .map((section, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveSectionIndex(index)}
                          className={`whitespace-nowrap px-4 py-2 font-medium ${
                            activeSectionIndex === index
                              ? "text-blue-600 border-b-2 border-blue-600"
                              : "text-gray-600 hover:text-blue-600"
                          }`}
                        >
                          {section.sectionName}
                        </button>
                      ))}
                  </div>
                  <button
                    onClick={slideRight}
                    disabled={
                      selectedIndex === questionnaireData?.sections?.length - 1
                    }
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>

                {/* Scrollable Questions Content */}
                <div
                  className="h-[50vh] overflow-auto p-4 bg-gray-50 rounded-lg"
                  style={{ maxHeight: "60vh" }}
                >
                  {questionnaireData?.sections[activeSectionIndex]?.questions
                    .sort((a, b) => a.order - b.order)
                    .map((question, qIndex) => {
                      return (
                        <div
                          key={qIndex}
                          className="mb-4 p-4 bg-white shadow rounded-md"
                        >
                          {/* Question Number and Content */}
                          <h4 className="font-semibold text-lg text-gray-800 mb-2">
                            {qIndex + 1}. {question.name}
                          </h4>

                          {/* Question Type */}
                          <p className="text-sm text-gray-500 mb-2">
                            Type: {question.type}
                          </p>

                          {/* Options with Roman Numerals */}
                          {question.questionOptions &&
                          question.questionOptions.length ? (
                            <ul className="list-none pl-4">
                              {question.questionOptions
                                .sort((a, b) => a.order - b.order)
                                .map((option, oIndex) => (
                                  <li
                                    key={oIndex}
                                    className="text-gray-700 flex items-center mb-1"
                                  >
                                    <span className="font-bold mr-2">
                                      {toRoman(oIndex)}.
                                    </span>
                                    {option?.name}
                                  </li>
                                ))}
                            </ul>
                          ) : (
                            <></>
                          )}
                        </div>
                      );
                    })}
                </div>
              </>
            )}
          </div>
        </div>
        {viewType === "write" ? (
          <div className="confirmationButtonGroup text-center pt-2 space-x-3">
            <Button
              icon="fa-solid fa-pen-to-square"
              type="iconButton"
              label={"Make Additional Changes"}
              backgroundColor={"transparent"}
              textColor="#1976d2"
              handler={makeChangesHandler}
            />
            <Button
              icon={"fas fa-check"}
              type="iconButton"
              label={"Mark Complete"}
              textColor="#1976d2"
              backgroundColor={"transparent"}
              handler={markCompleteHandler}
            />
          </div>
        ) : viewType === "modify" ? (
          <div className="confirmationButtonGroup text-center pt-2 space-x-3">
            <Button
              icon="fa-solid fa-pen-to-square"
              type="iconButton"
              label={"Create From This Template"}
              backgroundColor={"transparent"}
              textColor="#1976d2"
              handler={fromTemplateHandler}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
    </Common>
  );
}

export default ViewKIIQuestionnaire;
